import React, { useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { StockFilter as StockFilterType } from '../lib/types';

interface StockFilterProps {
  filter: StockFilterType;
  onFilterChange: (filter: StockFilterType) => void;
  operators?: Array<{ id: string; name: string }>;
  machines?: Array<{ id: string; name: string }>;
  products?: Array<{ id: string; name: string }>;
}

interface MultiSelectDropdownProps {
  label: string;
  options: Array<{ id: string; name: string }>;
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  color: 'blue' | 'green' | 'purple';
}

function MultiSelectDropdown({ label, options, selectedIds, onSelectionChange, color }: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const colorClasses = {
    blue: {
      badge: 'bg-blue-100 text-blue-800',
      badgeHover: 'hover:bg-blue-200',
      button: 'border-blue-300 hover:border-blue-400',
      checkbox: 'text-blue-600 focus:ring-blue-500'
    },
    green: {
      badge: 'bg-green-100 text-green-800',
      badgeHover: 'hover:bg-green-200',
      button: 'border-green-300 hover:border-green-400',
      checkbox: 'text-green-600 focus:ring-green-500'
    },
    purple: {
      badge: 'bg-purple-100 text-purple-800',
      badgeHover: 'hover:bg-purple-200',
      button: 'border-purple-300 hover:border-purple-400',
      checkbox: 'text-purple-600 focus:ring-purple-500'
    }
  };

  const colors = colorClasses[color];

  const handleSelectAll = () => {
    if (selectedIds.length === options.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(options.map(opt => opt.id));
    }
  };

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const allSelected = selectedIds.length === options.length && options.length > 0;
  const someSelected = selectedIds.length > 0 && selectedIds.length < options.length;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-48 px-3 py-2 text-sm border rounded-md bg-white ${colors.button} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${color}-500`}
      >
        <span className="text-gray-700">
          {selectedIds.length === 0 
            ? `Select ${label.toLowerCase()}` 
            : `${selectedIds.length} selected`}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-64 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-auto">
            {/* Select All Option */}
            <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-3 py-2">
              <label className="flex items-center cursor-pointer hover:bg-gray-100 py-1 px-1 rounded">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={handleSelectAll}
                  className={`h-4 w-4 rounded border-gray-300 ${colors.checkbox}`}
                />
                <span className="ml-2 text-sm font-semibold text-gray-900">
                  Select All
                </span>
              </label>
            </div>

            {/* Individual Options */}
            <div className="py-1">
              {options.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center cursor-pointer hover:bg-gray-50 px-3 py-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(option.id)}
                    onChange={() => handleToggle(option.id)}
                    className={`h-4 w-4 rounded border-gray-300 ${colors.checkbox}`}
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {option.name}
                  </span>
                </label>
              ))}
            </div>

            {/* Apply Button */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-3 py-2">
              <Button
                type="button"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="w-full"
              >
                Apply
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function StockFilter({ filter, onFilterChange, operators = [], machines = [], products = [] }: StockFilterProps) {
  const handleOperatorSelectionChange = (operatorIds: string[]) => {
    onFilterChange({
      ...filter,
      operatorIds,
    });
  };

  const handleMachineSelectionChange = (machineIds: string[]) => {
    onFilterChange({
      ...filter,
      machineIds,
    });
  };

  const handleProductSelectionChange = (productIds: string[]) => {
    onFilterChange({
      ...filter,
      productIds,
    });
  };

  const handleRemoveOperator = (operatorId: string) => {
    onFilterChange({
      ...filter,
      operatorIds: filter.operatorIds.filter(id => id !== operatorId),
    });
  };

  const handleRemoveMachine = (machineId: string) => {
    onFilterChange({
      ...filter,
      machineIds: filter.machineIds.filter(id => id !== machineId),
    });
  };

  const handleRemoveProduct = (productId: string) => {
    onFilterChange({
      ...filter,
      productIds: filter.productIds.filter(id => id !== productId),
    });
  };

  const handleClearAll = () => {
    onFilterChange({
      operatorIds: [],
      machineIds: [],
      productIds: [],
    });
  };

  const getFilteredMachines = () => {
    if (filter.operatorIds.length > 0) {
      return machines.filter(machine => 
        filter.operatorIds.some(opId => machine.id.startsWith(opId))
      );
    }
    return machines;
  };

  const getFilteredProducts = () => {
    if (filter.machineIds.length > 0) {
      return products.filter(product => 
        filter.machineIds.some(machineId => product.id.startsWith(machineId))
      );
    }
    return products;
  };

  const hasAnyFilters = filter.operatorIds.length > 0 || filter.machineIds.length > 0 || filter.productIds.length > 0;

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-6">
      <div className="space-y-4">
        {/* Header with Clear All button */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">Filter by:</span>
          {hasAnyFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Clear All
            </Button>
          )}
        </div>

        {/* Filter Options - Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Operator Filter */}
          <div className="flex flex-col gap-2">
            <span className="text-sm text-gray-600 font-medium">Operators</span>
            <MultiSelectDropdown
              label="Operators"
              options={operators}
              selectedIds={filter.operatorIds}
              onSelectionChange={handleOperatorSelectionChange}
              color="blue"
            />
            {filter.operatorIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {filter.operatorIds.map(opId => {
                  const operator = operators.find(op => op.id === opId);
                  return operator ? (
                    <div
                      key={opId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                    >
                      <span>{operator.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveOperator(opId)}
                        className="hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Machine Filter */}
          <div className="flex flex-col gap-2">
            <span className="text-sm text-gray-600 font-medium">Machines</span>
            <MultiSelectDropdown
              label="Machines"
              options={getFilteredMachines()}
              selectedIds={filter.machineIds}
              onSelectionChange={handleMachineSelectionChange}
              color="green"
            />
            {filter.machineIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {filter.machineIds.map(machineId => {
                  const machine = machines.find(m => m.id === machineId);
                  return machine ? (
                    <div
                      key={machineId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                    >
                      <span>{machine.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveMachine(machineId)}
                        className="hover:bg-green-200 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Product Filter */}
          <div className="flex flex-col gap-2">
            <span className="text-sm text-gray-600 font-medium">Products</span>
            <MultiSelectDropdown
              label="Products"
              options={getFilteredProducts()}
              selectedIds={filter.productIds}
              onSelectionChange={handleProductSelectionChange}
              color="purple"
            />
            {filter.productIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {filter.productIds.map(productId => {
                  const product = products.find(p => p.id === productId);
                  return product ? (
                    <div
                      key={productId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs"
                    >
                      <span>{product.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveProduct(productId)}
                        className="hover:bg-purple-200 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            )}
          </div>
        </div>

        {/* Active Filter Summary */}
        {hasAnyFilters && (
          <div className="pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Active filters: {filter.operatorIds.length > 0 && `${filter.operatorIds.length} operator${filter.operatorIds.length > 1 ? 's' : ''}`}
              {filter.operatorIds.length > 0 && (filter.machineIds.length > 0 || filter.productIds.length > 0) && ', '}
              {filter.machineIds.length > 0 && `${filter.machineIds.length} machine${filter.machineIds.length > 1 ? 's' : ''}`}
              {filter.machineIds.length > 0 && filter.productIds.length > 0 && ', '}
              {filter.productIds.length > 0 && `${filter.productIds.length} product${filter.productIds.length > 1 ? 's' : ''}`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}